import { useQuery, useMutation } from '@tanstack/react-query';
import { apiClient } from '@shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';

// ==================== Types ====================

export interface Prefecture {
  id: number;
  name: string;
}

export interface City {
  id: number;
  name: string;
  kana: string;
}

export interface Town {
  id: number;
  name: string;
  kana: string;
  postal_code1: string;
  postal_code2: string;
  postal_code: string;
  prefecture_name: string;
  city_name: string;
  town_name?: string;
  full_address: string;
  full_address_kana: string;
}

export interface AddressResult {
  postal_code1: string;
  postal_code2: string;
  address: string;
  address_kana: string;
  prefecture_id?: number;
  city_id?: number;
  town_id?: number;
}

// ==================== API Response Types ====================

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

interface PrefecturesData {
  prefectures: Prefecture[];
}

interface CitiesData {
  cities: City[];
}

interface TownsData {
  towns: Town[];
}

interface AddressSearchData {
  results: Town[];
}

// ==================== Address Context Type ====================

export interface AddressContextConfig {
  siteType: string;
  clinicNumber?: string;
  isAdmin?: boolean;
}

// ==================== Endpoint Builder ====================

function buildAddressEndpoint(config: AddressContextConfig, endpoint: string): string {
  const { siteType, clinicNumber, isAdmin } = config;

  if (isAdmin) {
    return `/admin/${siteType}/master-data/addresses${endpoint}`;
  }

  return clinicApiPath(siteType, clinicNumber || '', `/addresses${endpoint}`);
}

// ==================== Hooks ====================

export function usePrefectures(config: AddressContextConfig, enabled: boolean = true) {
  return useQuery({
    queryKey: ['prefectures', config.siteType, config.clinicNumber, config.isAdmin],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<PrefecturesData>>(
        buildAddressEndpoint(config, '/prefectures')
      );
      return data.data.prefectures || [];
    },
    enabled: !!config.siteType && enabled,
  });
}

export function useCities(config: AddressContextConfig) {
  return useMutation({
    mutationFn: async (prefId: number) => {
      const { data } = await apiClient.post<ApiResponse<CitiesData>>(
        buildAddressEndpoint(config, '/cities'),
        { prefecture_id: prefId }
      );
      return data.data.cities || [];
    },
  });
}

export function useTowns(config: AddressContextConfig) {
  return useMutation({
    mutationFn: async (cityId: number) => {
      const { data } = await apiClient.post<ApiResponse<TownsData>>(
        buildAddressEndpoint(config, '/towns'),
        { city_id: cityId }
      );
      return data.data.towns || [];
    },
  });
}

export function useAddressSearch(config: AddressContextConfig) {
  return useMutation({
    mutationFn: async (params: {
      postal_code1?: string;
      postal_code2?: string;
      address?: string;
    }) => {
      const { data } = await apiClient.post<ApiResponse<AddressSearchData>>(
        buildAddressEndpoint(config, '/search'),
        params
      );
      return data.data.results || [];
    },
  });
}
